import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, type Approval } from '../api/client';
import { ConfirmDialog } from '../components/ConfirmDialog';

export function Approvals() {
  const { currentWorkspaceId: workspaceId } = useAuth();

  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [denyTarget, setDenyTarget] = useState<string | null>(null);

  async function loadApprovals() {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getApprovals(workspaceId);
      setApprovals(data.approvals);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load approvals.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadApprovals();
  }, [workspaceId]);

  async function handleApprove(approvalId: string) {
    if (!workspaceId) return;
    setActionInProgress(approvalId);
    try {
      await api.approveApproval(workspaceId, approvalId);
      setApprovals((prev) => prev.filter((a) => a.approval_id !== approvalId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve.');
    } finally {
      setActionInProgress(null);
    }
  }

  async function handleDeny(approvalId: string) {
    if (!workspaceId) return;
    setActionInProgress(approvalId);
    try {
      await api.denyApproval(workspaceId, approvalId);
      setApprovals((prev) => prev.filter((a) => a.approval_id !== approvalId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deny.');
    } finally {
      setActionInProgress(null);
    }
  }

  if (!workspaceId) {
    return (
      <div className="rounded-xl border border-border-dark bg-surface-1 p-8 text-center text-sm text-text-secondary">
        No workspace found. Please complete onboarding first.
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary">Approvals</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Review and approve or deny pending tool call requests
        </p>
        <p className="mt-2 text-xs text-text-tertiary">
          Keyboard shortcuts: <kbd className="rounded border border-border-dark bg-surface-2 px-1.5 py-0.5 font-mono text-text-secondary">A</kbd> approve &middot; <kbd className="rounded border border-border-dark bg-surface-2 px-1.5 py-0.5 font-mono text-text-secondary">D</kbd> deny &middot; <kbd className="rounded border border-border-dark bg-surface-2 px-1.5 py-0.5 font-mono text-text-secondary">J</kbd>/<kbd className="rounded border border-border-dark bg-surface-2 px-1.5 py-0.5 font-mono text-text-secondary">K</kbd> navigate
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-[rgba(248,113,113,0.2)] bg-[rgba(248,113,113,0.12)] px-4 py-3 text-sm text-[var(--color-error)]">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 spinner-gradient" />
        </div>
      ) : approvals.length === 0 ? (
        <div className="rounded-xl border border-border-dark bg-surface-1 p-12 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[rgba(6,182,164,0.12)]">
            <svg className="h-6 w-6 text-accent-teal" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
          </div>
          <p className="text-sm font-medium text-text-primary">No pending approvals</p>
          <p className="mt-1 text-sm text-text-secondary">
            All tool call requests have been processed.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border-dark bg-surface-1">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border-dark bg-surface-0">
                <th className="px-4 py-3 font-medium text-text-secondary">Tool</th>
                <th className="px-4 py-3 font-medium text-text-secondary">Capability</th>
                <th className="px-4 py-3 font-medium text-text-secondary">Actor</th>
                <th className="px-4 py-3 font-medium text-text-secondary">Reason</th>
                <th className="px-4 py-3 font-medium text-text-secondary">Requested</th>
                <th className="px-4 py-3 font-medium text-text-secondary">Expires</th>
                <th className="px-4 py-3 text-right font-medium text-text-secondary">Actions</th>
              </tr>
            </thead>
            <tbody>
              {approvals.map((approval) => {
                const isActioning = actionInProgress === approval.approval_id;
                return (
                  <tr key={approval.approval_id} className="border-b border-border-subtle last:border-0">
                    <td className="px-4 py-3 font-medium text-text-primary">
                      {approval.tool_name}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        approval.tool_capability === 'read'
                          ? 'bg-[rgba(6,182,164,0.12)] text-accent-teal'
                          : approval.tool_capability === 'write'
                            ? 'bg-[rgba(251,191,36,0.12)] text-[var(--color-warning)]'
                            : approval.tool_capability === 'delete'
                              ? 'bg-[rgba(248,113,113,0.12)] text-[var(--color-error)]'
                              : approval.tool_capability === 'admin'
                                ? 'bg-[rgba(139,92,246,0.12)] text-accent-violet'
                                : 'bg-surface-2 text-text-primary'
                      }`}>
                        {approval.tool_capability}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-text-secondary">
                      {approval.actor_id}
                    </td>
                    <td className="max-w-xs truncate px-4 py-3 text-text-secondary">
                      {approval.reason}
                    </td>
                    <td className="px-4 py-3 text-text-secondary">
                      {new Date(approval.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-text-secondary">
                      {new Date(approval.expires_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleApprove(approval.approval_id)}
                          disabled={isActioning}
                          className="rounded-md bg-[var(--color-success)] px-3 py-1.5 text-xs font-medium text-text-primary transition-colors hover:opacity-90 disabled:opacity-50"
                        >
                          {isActioning ? '...' : 'Approve'}
                        </button>
                        <button
                          onClick={() => setDenyTarget(approval.approval_id)}
                          disabled={isActioning}
                          className="rounded-md bg-[var(--color-error)] px-3 py-1.5 text-xs font-medium text-text-primary transition-colors hover:opacity-90 disabled:opacity-50"
                        >
                          {isActioning ? '...' : 'Deny'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={denyTarget !== null}
        title="Deny Request"
        message="This tool call request will be permanently denied. The requesting agent will receive a denial response."
        confirmLabel="Deny Request"
        confirmVariant="danger"
        onConfirm={() => {
          if (denyTarget) handleDeny(denyTarget);
          setDenyTarget(null);
        }}
        onCancel={() => setDenyTarget(null)}
      />
    </div>
  );
}
